import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export function visibleSearchPages(currentPage: number, totalPages: number, limit: number): number[] {
  const count = Math.min(totalPages, limit);
  const start = Math.max(1, Math.min(currentPage - Math.floor(count / 2), totalPages - count + 1));
  return Array.from({ length: count }, (_, index) => start + index);
}

interface SearchPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function SearchPagination({ currentPage, totalPages, onPageChange }: SearchPaginationProps) {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;
  const mobilePages = new Set(visibleSearchPages(currentPage, totalPages, 3));
  const navigationButton = "flex min-h-11 min-w-0 items-center justify-center gap-1 rounded-full border border-border px-2 text-xs font-semibold hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 sm:px-3 sm:text-sm";

  return (
    <nav
      aria-label={t("pagination.navigation", { defaultValue: "Search result pages" })}
      className="mt-8 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1 sm:flex sm:justify-center sm:gap-2"
    >
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        aria-label={t("pagination.previous_page", { defaultValue: "Previous page" })}
        className={navigationButton}
      >
        <ChevronLeft aria-hidden="true" className="hidden h-4 w-4 shrink-0 sm:block" />
        <span>{t("photoGallery.previous", { defaultValue: "Previous" })}</span>
      </button>
      <div className="flex items-center gap-1 sm:gap-2">
        {visibleSearchPages(currentPage, totalPages, 7).map(page => (
          <button
            key={page}
            type="button"
            onClick={() => { if (page !== currentPage) onPageChange(page); }}
            aria-label={t("pagination.page", { defaultValue: "Page {{page}}", page })}
            aria-current={page === currentPage ? "page" : undefined}
            className={cn(
              "h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              mobilePages.has(page) ? "flex" : "hidden sm:flex",
              page === currentPage ? "bg-primary text-primary-foreground" : "border border-border hover:bg-muted",
            )}
          >
            {page}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        aria-label={t("pagination.next_page", { defaultValue: "Next page" })}
        className={navigationButton}
      >
        <span>{t("common.next", { defaultValue: "Next" })}</span>
        <ChevronRight aria-hidden="true" className="hidden h-4 w-4 shrink-0 sm:block" />
      </button>
    </nav>
  );
}
